/*!
 * AMOUR amour.si — Qikify Slide Cart enrichment
 *
 * What this script does inside the Qikify Slide Cart drawer (only):
 *   1. Adds an "inspiriran po …" subtitle below each cart line item title
 *      (and below recommended products in the drawer) using metafields read
 *      from the alternate Liquid view templates/product.cartdata.liquid.
 *      (The circular inspired-by image badge was removed by request —
 *      text subtitle only.)
 *
 * Scope rules — strictly drawer-only:
 *   - Drawer roots are matched by an explicit allow-list (`.qsc2-mini-cart`
 *     etc.). We must NOT match Sticky-Add-to-Cart, body-level qsc2 markers,
 *     collection grids, search dropdowns, or anything outside the drawer.
 *   - In the drawer, two enrichment passes run:
 *        Pass A — cart line items: ancestors that contain a quantity input
 *                 or +/- button (excludes recommendations).
 *        Pass B — recommendation cards: any `.qsc2-r-product` element
 *                 (Qikify's own class for cart-upsell items).
 *   - Each container is enhanced at most once via a dataset marker.
 *   - The subtitle is inserted right after the title anchor (the `<a>` with
 *     the most text content), never inside the image column.
 *
 * Defensive guarantees:
 *   - Host-guarded (amour.si + the matching .myshopify.com preview host).
 *   - All DOM work wrapped in try/catch — never throws into the page.
 *   - Idempotent re-scans (debounced 60 ms) on MutationObserver bursts.
 *   - Image swap is detected from `src` value (works even if the row is
 *     re-rendered by Qikify and our previous markers are wiped).
 */
(function () {
  'use strict';

  // Allow-list of AMOUR storefront domains. Both amour.si (single-locale)
  // and the multi-locale amourparfums.* family use Qikify Slide Cart and
  // need the same inspired-by enrichment. The .myshopify.com hosts are
  // included for theme-editor preview iframes (each store has its own).
  var HOST_OK =
    /(^|\.)amour\.si$/i.test(window.location.hostname) ||
    /(^|\.)amour-parfums-2\.myshopify\.com$/i.test(window.location.hostname) ||
    /(^|\.)70zw21-rn\.myshopify\.com$/i.test(window.location.hostname) ||
    /(^|\.)amourparfums\.(com|de|hr|it|nl|at)$/i.test(window.location.hostname) ||
    /(^|\.)amour\.hu$/i.test(window.location.hostname) ||
    /(^|\.)parfumerija-amour\.myshopify\.com$/i.test(window.location.hostname);
  if (!HOST_OK) return;

  // --- Slide-cart-drawer selector. Intentionally strict — must NOT match
  // the Sticky Add-to-Cart app wrapper or any body-level qsc2 marker. ---
  var DRAWER_SELECTORS = [
    '.qsc2-mini-cart',
    '.qsc2-cart-drawer',
    '.qsc2-slidecart',
    '[data-qsc2-drawer]',
  ].join(',');

  // Selectors that, when present *inside* a candidate container, prove it is
  // a cart line item (vs. a recommendation card / sticky cart card / etc).
  var QTY_CONTROL_SELECTORS = [
    'input[type="number"]',
    'input[name*="quantity" i]',
    'input[name*="updates" i]',
    'button[class*="qty" i]',
    'button[class*="quantity" i]',
    '[class*="quantity-input" i]',
    '[class*="qty-input" i]',
  ].join(',');

  var REC_SELECTORS = ['.qsc2-r-product'].join(',');

  var CACHE = new Map();
  var IN_FLIGHT = new Map();
  var STYLE_INJECTED = false;

  function injectStyleOnce() {
    if (STYLE_INJECTED) return;
    STYLE_INJECTED = true;
    var css =
      // Subtitle under the title
      '.amour-inspired-subtitle{display:block;margin:4px 0 0;' +
      'font-size:12px;line-height:1.35;font-weight:400;color:#5c5c5c;' +
      'word-break:break-word;}' +
      '.amour-inspired-subtitle .amour-inspired-label{margin-right:.25em;}' +
      '.amour-inspired-subtitle .amour-inspired-detail{font-weight:500;color:#3a3a3a;}' +
      // Badge removed by request — hide any stale instances defensively
      '.amour-inspired-badge{display:none !important;}';
    var style = document.createElement('style');
    style.setAttribute('data-amour-inspired', 'styles');
    style.appendChild(document.createTextNode(css));
    (document.head || document.documentElement).appendChild(style);
  }

  // Locale-aware route prefix.
  //
  // amourparfums.com is multi-locale: the same Shopify store serves /
  // (English), /hu (Hungarian), /de (German), /it (Italian), /hr
  // (Croatian) on URL paths, plus the country-domain variants
  // amourparfums.de / .it / .hr / .nl / .at and amour.hu where the
  // domain itself maps to a locale (no prefix). Fetching `/products/X`
  // without the locale prefix returns the *default* locale render
  // (English on amourparfums.com), so cartdata.label resolves to
  // "Perfume inspired by:" even when the visitor is on /hu — the user
  // then sees an English label inside the Hungarian cart drawer.
  //
  // `Shopify.routes.root` is set by Shopify and ends with "/" — it is
  // "/" on default-locale renders and "/hu/" / "/de/" on
  // path-prefixed locales, exactly the prefix we need to prepend so
  // the cartdata view renders in the customer's current locale.
  function localizeRoute(path) {
    var root = '/';
    try {
      if (window.Shopify && Shopify.routes && Shopify.routes.root) {
        root = Shopify.routes.root;
      }
    } catch (_) { /* never throw out of helper */ }
    if (path.charAt(0) === '/') path = path.slice(1);
    if (root.charAt(root.length - 1) !== '/') root = root + '/';
    return root + path;
  }

  function fetchInspired(handle) {
    if (CACHE.has(handle)) return Promise.resolve(CACHE.get(handle));
    if (IN_FLIGHT.has(handle)) return IN_FLIGHT.get(handle);

    var url = localizeRoute('/products/' + encodeURIComponent(handle) + '?view=cartdata');
    var p = fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json,text/plain;q=0.9,*/*;q=0.5' },
    })
      .then(function (r) {
        if (!r || !r.ok) throw new Error('cartdata HTTP ' + (r && r.status));
        return r.text();
      })
      .then(function (text) {
        var trimmed = (text || '').trim();
        if (!trimmed || trimmed.charAt(0) !== '{') throw new Error('cartdata not JSON');
        var data;
        try {
          data = JSON.parse(trimmed);
        } catch (_) {
          throw new Error('cartdata parse failed');
        }
        CACHE.set(handle, data);
        return data;
      })
      .catch(function () {
        CACHE.set(handle, null);
        return null;
      });

    IN_FLIGHT.set(handle, p);
    return p;
  }

  function buildSubtitle(data) {
    if (!data) return null;
    // AMOUR Parfums originals (deklaracije_vendor = "AMOUR PARFUMS") are
    // not inspired by anything external. cartdata.liquid signals this by
    // returning has_inspired:false. Without this guard the drawer would
    // render the misleading "Parfum, navdihnjen po: AMOUR No. 517" line
    // (since fragrance is the product title for in-house originals).
    if (data.has_inspired === false) return null;
    var dek = (data.dek || '').toString().trim();
    var frag = (data.fragrance || '').toString().trim();
    if (!dek && !frag) return null;

    var detail = dek && frag ? dek + ' \u2013 ' + frag : dek || frag;
    var label = (data.label || '').toString().trim();

    var wrap = document.createElement('div');
    wrap.className = 'amour-inspired-subtitle';

    if (label) {
      var lblSpan = document.createElement('span');
      lblSpan.className = 'amour-inspired-label';
      lblSpan.textContent = label;
      wrap.appendChild(lblSpan);
    }

    var detSpan = document.createElement('span');
    detSpan.className = 'amour-inspired-detail';
    detSpan.textContent = detail;
    wrap.appendChild(detSpan);

    return wrap;
  }

  function extractHandleFromLink(a) {
    var href = a && a.getAttribute && a.getAttribute('href');
    if (!href) return null;
    var m = href.match(/\/products\/([^/?#]+)/i);
    return m ? decodeURIComponent(m[1]) : null;
  }

  /** Walk up from anchor to find smallest ancestor containing a qty control. */
  function findLineItemContainer(anchor, drawerRoot) {
    var node = anchor.parentElement;
    var depth = 0;
    while (node && node !== drawerRoot && depth < 8) {
      if (node.querySelector(QTY_CONTROL_SELECTORS)) return node;
      node = node.parentElement;
      depth++;
    }
    return null;
  }

  /** Pick the title anchor (richest text) from product links in a row. */
  function pickTitleAnchor(container) {
    var anchors = container.querySelectorAll('a[href*="/products/"]');
    if (!anchors.length) return null;
    var best = anchors[0];
    var bestLen = (best.textContent || '').trim().length;
    for (var i = 1; i < anchors.length; i++) {
      var a = anchors[i];
      var len = (a.textContent || '').trim().length;
      if (len > bestLen) {
        best = a;
        bestLen = len;
      }
    }
    return best;
  }

  function insertSubtitle(container, titleAnchor, data) {
    var node = buildSubtitle(data);
    if (!node) return;
    // Remove any prior subtitle we previously injected in this row
    container.querySelectorAll('.amour-inspired-subtitle[data-amour="injected"]').forEach(
      function (n) {
        n.remove();
      },
    );
    node.setAttribute('data-amour', 'injected');
    injectStyleOnce();
    var parent = titleAnchor.parentElement || container;
    var after = titleAnchor.nextSibling;
    if (after) parent.insertBefore(node, after);
    else parent.appendChild(node);
  }

  function enhance(container) {
    if (!container || container.dataset.amourInspired) return;

    var titleAnchor = pickTitleAnchor(container);
    if (!titleAnchor) return;

    var handle = extractHandleFromLink(titleAnchor);
    if (!handle) return;

    container.dataset.amourInspired = 'pending';
    fetchInspired(handle).then(function (data) {
      if (!container.isConnected) return;
      try {
        insertSubtitle(container, titleAnchor, data);
      } catch (_) {
        /* never throw into the page */
      }
      container.dataset.amourInspired = 'done';
    });
  }

  function scan() {
    var drawers = document.querySelectorAll(DRAWER_SELECTORS);
    if (!drawers.length) return;

    var seen = new Set();
    drawers.forEach(function (drawer) {
      // Pass A — cart line items (must have qty controls)
      var anchors = drawer.querySelectorAll('a[href*="/products/"]');
      anchors.forEach(function (a) {
        try {
          var container = findLineItemContainer(a, drawer);
          if (!container || seen.has(container)) return;
          seen.add(container);
          enhance(container);
        } catch (_) {
          /* swallow */
        }
      });

      // Pass B — recommendation cards inside the drawer
      drawer.querySelectorAll(REC_SELECTORS).forEach(function (rec) {
        if (seen.has(rec)) return;
        seen.add(rec);
        try {
          enhance(rec);
        } catch (_) {
          /* swallow */
        }
      });
    });
  }

  var scanTimer = null;
  function scheduleScan() {
    if (scanTimer) clearTimeout(scanTimer);
    scanTimer = setTimeout(scan, 60);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleScan, { once: true });
  } else {
    scheduleScan();
  }

  var mo = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var t = mutations[i].target;
      if (
        t &&
        t.nodeType === 1 &&
        (t.classList.contains('amour-inspired-subtitle') ||
          t.classList.contains('amour-inspired-badge') ||
          t.closest('.amour-inspired-subtitle, .amour-inspired-badge'))
      ) {
        continue;
      }
      scheduleScan();
      return;
    }
  });
  mo.observe(document.body || document.documentElement, {
    childList: true,
    subtree: true,
  });
})();
